import { useCallback, useEffect, useState } from 'react'
import { api, auth } from '../api/client'
import type { UserSummary } from '../api/types'
import { Card } from './Card'
import { Empty, ErrorBanner, Loading } from './Common'
import { timeAgo } from '../theme/charts'

/**
 * Account management.
 *
 * <p>Two cards: the account list for administrators, and a change-your-own-
 * password form for everybody. The second is not a courtesy — the bootstrap
 * account starts with a generated password, and if changing it required an
 * administrator to act on your behalf, it would never get changed.
 */
export function UserAdmin({ isAdmin }: { isAdmin: boolean }) {
  return (
    <>
      {isAdmin && <UserList />}
      <OwnPassword />
    </>
  )
}

function UserList() {
  const [users, setUsers] = useState<UserSummary[] | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const load = useCallback(async () => {
    try {
      setUsers(await api.users())
      setError(null)
    } catch (e) {
      setError(e)
    }
  }, [])

  useEffect(() => { load() }, [load])

  async function act(username: string, fn: () => Promise<unknown>) {
    setBusy(username)
    setError(null)
    try {
      await fn()
      await load()
    } catch (e) {
      setError(e)
    } finally {
      setBusy(null)
    }
  }

  const admins = (users ?? []).filter((u) => u.role === 'admin' && u.enabled).length

  return (
    <Card
      title="User accounts"
      prefKey="admin.users"
      meta={users ? `${users.length} account${users.length === 1 ? '' : 's'}` : undefined}
      note="Accounts that can sign in to this dashboard. The last enabled administrator cannot be demoted, disabled or deleted, and you cannot lock yourself out — those actions are refused rather than silently ignored."
    >
      <>
        <ErrorBanner error={error} />

        {!users ? <Loading what="accounts" /> : users.length === 0 ? (
          <Empty>No accounts. This should not happen — one is created on first start.</Empty>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Name</th>
                <th>Role</th>
                <th>Status</th>
                <th className="num">Last sign-in</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                // Spell out why an action is unavailable rather than just
                // disabling the control.
                const lastAdmin = u.role === 'admin' && u.enabled && admins <= 1
                const lockSelf = u.self
                const why = lockSelf
                  ? 'This is your own account'
                  : lastAdmin ? 'The only enabled administrator' : ''
                return (
                  <tr key={u.id}>
                    <td>
                      <strong>{u.username}</strong>
                      {u.self && <span className="muted"> (you)</span>}
                    </td>
                    <td className="muted">{u.displayName || '—'}</td>
                    <td>
                      <select
                        value={u.role}
                        disabled={busy === u.username || lockSelf || lastAdmin}
                        title={why}
                        onChange={(e) => act(u.username,
                          () => api.updateUser(u.username, { role: e.target.value }))}
                      >
                        <option value="admin">admin</option>
                        <option value="viewer">viewer</option>
                      </select>
                    </td>
                    <td>
                      <span style={{
                        color: u.enabled ? 'var(--status-ok)' : 'var(--text-muted)',
                        fontWeight: 600, fontSize: 'var(--text-xs)',
                      }}>
                        {u.enabled ? 'enabled' : 'disabled'}
                      </span>
                    </td>
                    <td className="num muted nowrap">{timeAgo(u.lastLoginAt)}</td>
                    <td>
                      <div className="row" style={{ gap: 'var(--space-1)', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          disabled={busy === u.username || lockSelf || lastAdmin}
                          title={why}
                          onClick={() => act(u.username,
                            () => api.updateUser(u.username, { enabled: !u.enabled }))}
                        >
                          {u.enabled ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          type="button"
                          disabled={busy === u.username}
                          onClick={() => {
                            const pw = window.prompt(
                              `New password for ${u.username} (at least 10 characters):`)
                            if (pw) act(u.username, () => api.setUserPassword(u.username, pw))
                          }}
                        >
                          Reset password
                        </button>
                        <button
                          type="button"
                          disabled={busy === u.username || lockSelf || lastAdmin}
                          title={why}
                          onClick={() => {
                            if (window.confirm(
                              `Delete ${u.username}? Their alert history is unaffected.`)) {
                              act(u.username, () => api.deleteUser(u.username))
                            }
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}

        <div style={{ marginTop: 'var(--space-4)' }}>
          {adding ? (
            <AddUser onDone={() => { setAdding(false); load() }} onCancel={() => setAdding(false)} />
          ) : (
            <button type="button" onClick={() => setAdding(true)}>Add user</button>
          )}
        </div>
      </>
    </Card>
  )
}

function AddUser({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const [username, setUsername] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('viewer')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.createUser({ username, password, displayName, email, role })
      onDone()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} style={{
      border: '1px solid var(--border-hairline)',
      borderRadius: 'var(--radius-md)',
      padding: 'var(--space-4)',
      background: 'var(--surface-2)',
    }}>
      <ErrorBanner error={error} />
      <div className="grid grid-3" style={{ gap: 'var(--space-3)' }}>
        <div className="field">
          <label htmlFor="nu-username">Username</label>
          <input id="nu-username" value={username} required autoFocus
                 onChange={(e) => setUsername(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="nu-name">Display name</label>
          <input id="nu-name" value={displayName}
                 onChange={(e) => setDisplayName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="nu-email">Email</label>
          <input id="nu-email" type="email" value={email}
                 onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="nu-role">Role</label>
          <select id="nu-role" value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="viewer">viewer — read only</option>
            <option value="admin">admin — can manage accounts and mail</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="nu-pw">Password</label>
          <input id="nu-pw" type="password" value={password} required minLength={10}
                 onChange={(e) => setPassword(e.target.value)} />
          <span className="muted" style={{ fontSize: 'var(--text-xs)' }}>
            At least 10 characters.
          </span>
        </div>
      </div>
      <div className="row" style={{ gap: 'var(--space-2)' }}>
        <button type="submit" className="btn-primary" style={{ width: 'auto' }} disabled={busy}>
          {busy ? 'Creating…' : 'Create user'}
        </button>
        <button type="button" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </form>
  )
}

function OwnPassword() {
  const user = auth.user()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<unknown>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    setDone(false)
    try {
      await api.changeOwnPassword(current, next)
      setCurrent('')
      setNext('')
      setDone(true)
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title="Change your password" prefKey="admin.ownPassword" defaultCollapsed
          meta={user ? `signed in as ${user.username}` : undefined}>
      <form onSubmit={submit}>
        <ErrorBanner error={error} />
        {done && (
          <div className="banner" style={{
            background: 'var(--status-ok-bg)',
            borderColor: 'var(--status-ok-fill)',
            color: 'var(--status-ok)',
          }}>
            Password changed. Your existing session stays valid.
          </div>
        )}
        <div className="grid grid-2" style={{ gap: 'var(--space-3)' }}>
          <div className="field">
            <label htmlFor="pw-current">Current password</label>
            <input id="pw-current" type="password" value={current} required
                   autoComplete="current-password"
                   onChange={(e) => setCurrent(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="pw-new">New password</label>
            <input id="pw-new" type="password" value={next} required minLength={10}
                   autoComplete="new-password"
                   onChange={(e) => setNext(e.target.value)} />
          </div>
        </div>
        <button type="submit" className="btn-primary" style={{ width: 'auto' }}
                disabled={busy || !current || !next}>
          {busy ? 'Changing…' : 'Change password'}
        </button>
      </form>
    </Card>
  )
}
